/**
 * React Query Hooks for Chat Operations
 * Provides data fetching, caching, and state management for chats and messages
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import * as chatService from '@/api/services/chat.service';
import type {
  ChatCreate,
  ChatUpdate,
  MessageCreate,
  MessageUpdate,
  LikeType,
  SubscriptionCreate,
  SubscriptionUpdate,
  MessageQueryParams,
  ChatMemberRole,
} from '@/api/types/chat.types';

// ============================================================================
// Query Keys
// ============================================================================

export const chatKeys = {
  all: ['chats'] as const,
  lists: () => [...chatKeys.all, 'list'] as const,
  list: () => [...chatKeys.lists()] as const,
  details: () => [...chatKeys.all, 'detail'] as const,
  detail: (id: string) => [...chatKeys.details(), id] as const,
  messages: (chatId: string) =>
    [...chatKeys.detail(chatId), 'messages'] as const,
  likes: (messageId: string) => ['messages', messageId, 'likes'] as const,
  subscriptions: (chatId: string) =>
    [...chatKeys.detail(chatId), 'subscriptions'] as const,
  mySubscriptions: () => [...chatKeys.all, 'my-subscriptions'] as const,
  members: (chatId: string) => [...chatKeys.detail(chatId), 'members'] as const,
  oneToOne: (userId: string) =>
    [...chatKeys.all, 'one-to-one', userId] as const,
};

// ============================================================================
// Chat Hooks
// ============================================================================

/**
 * Get list of all chats
 */
export function useChats() {
  return useQuery({
    queryKey: chatKeys.list(),
    queryFn: chatService.listChats,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get single chat by ID
 */
export function useChat(chatId: string | null) {
  return useQuery({
    queryKey: chatKeys.detail(chatId || ''),
    queryFn: () => chatService.getChat(chatId!),
    enabled: !!chatId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get or create global chat
 * @deprecated Use useGeneralChatMessages for lazy initialization
 */
export function useGlobalChat() {
  return useQuery({
    queryKey: chatKeys.detail('global'),
    queryFn: chatService.getGlobalChat,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Get messages for general chat (lazy initialization with pagination)
 * Returns paginated response if chat doesn't exist yet
 */
export function useGeneralChatMessages(params?: MessageQueryParams) {
  return useQuery({
    queryKey: [...chatKeys.messages('general'), params],
    queryFn: () => chatService.listGeneralChatMessages(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Infinite scroll hook for general chat messages
 * Loads messages progressively as user scrolls
 */
export function useInfiniteGeneralChatMessages(initialLimit = 30) {
  return useInfiniteQuery({
    queryKey: ['chat', 'messages', 'general', 'infinite'],
    queryFn: async ({ pageParam }) => {
      return chatService.listGeneralChatMessages({
        limit: initialLimit,
        before_message_id: pageParam as string | undefined,
      });
    },
    getNextPageParam: (lastPage) => {
      // Return the oldest message ID if there are more messages to load
      return lastPage.pagination.has_more
        ? lastPage.pagination.oldest_message_id
        : undefined;
    },
    initialPageParam: undefined as string | undefined,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Send message to general chat (lazy initialization)
 * Creates chat automatically if it doesn't exist
 */
export function useSendGeneralChatMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MessageCreate) =>
      chatService.sendGeneralChatMessage(data),
    onSuccess: () => {
      // Invalidate messages list to refetch
      queryClient.invalidateQueries({ queryKey: chatKeys.messages('general') });
    },
  });
}

/**
 * Create a new chat
 */
export function useCreateChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChatCreate) => chatService.createChat(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    },
  });
}

/**
 * Update a chat
 */
export function useUpdateChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, data }: { chatId: string; data: ChatUpdate }) =>
      chatService.updateChat(chatId, data),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.detail(chatId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    },
  });
}

/**
 * Delete a chat
 */
export function useDeleteChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: string) => chatService.deleteChat(chatId),
    onSuccess: (_, chatId) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
      queryClient.removeQueries({ queryKey: chatKeys.detail(chatId) });
    },
  });
}

// ============================================================================
// Message Hooks
// ============================================================================

/**
 * Get messages for a chat (with pagination)
 */
export function useChatMessages(
  chatId: string | null,
  params?: MessageQueryParams
) {
  return useQuery({
    queryKey: [...chatKeys.messages(chatId || ''), params],
    queryFn: () => chatService.listMessages(chatId!, params),
    enabled: !!chatId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Send a message
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, data }: { chatId: string; data: MessageCreate }) =>
      chatService.sendMessage(chatId, data),
    onSuccess: (_, { chatId }) => {
      // Invalidate messages list to refetch
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(chatId) });
    },
  });
}

/**
 * Update a message
 */
export function useUpdateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      data,
    }: {
      messageId: string;
      data: MessageUpdate;
    }) => chatService.updateMessage(messageId, data),
    onSuccess: (message) => {
      // Invalidate messages list for this chat
      queryClient.invalidateQueries({
        queryKey: chatKeys.messages(message.chat_id),
      });
    },
  });
}

/**
 * Archive a message
 */
export function useArchiveMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId }: { messageId: string; chatId: string }) =>
      chatService.archiveMessage(messageId),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(chatId) });
    },
  });
}

/**
 * Reply to a message
 */
export function useReplyToMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      message,
    }: {
      messageId: string;
      message: string;
    }) => chatService.replyToMessage(messageId, message),
    onSuccess: (message) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.messages(message.chat_id),
      });
    },
  });
}

// ============================================================================
// Reaction/Like Hooks
// ============================================================================

/**
 * Get likes for a message
 */
export function useMessageLikes(
  messageId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: chatKeys.likes(messageId || ''),
    queryFn: () => chatService.listLikes(messageId!),
    enabled: options?.enabled !== undefined ? options.enabled : !!messageId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Add a like/reaction to a message
 */
export function useAddLike() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      likeType,
    }: {
      messageId: string;
      likeType: LikeType;
    }) => chatService.addLike(messageId, likeType),
    onSuccess: (_, { messageId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.likes(messageId) });
    },
  });
}

/**
 * Remove a like/reaction from a message
 */
export function useRemoveLike() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      likeType,
    }: {
      messageId: string;
      likeType: LikeType;
    }) => chatService.removeLike(messageId, likeType),
    onSuccess: (_, { messageId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.likes(messageId) });
    },
  });
}

/**
 * Toggle a like/reaction on a message
 * Adds if not present, removes if already present
 */
export function useToggleLike() {
  const addLike = useAddLike();
  const removeLike = useRemoveLike();

  return {
    mutate: async ({
      messageId,
      likeType,
      hasLike,
    }: {
      messageId: string;
      likeType: LikeType;
      hasLike: boolean;
    }) => {
      if (hasLike) {
        await removeLike.mutateAsync({ messageId, likeType });
      } else {
        await addLike.mutateAsync({ messageId, likeType });
      }
    },
    isLoading: addLike.isPending || removeLike.isPending,
  };
}

// ============================================================================
// Read Receipt Hooks
// ============================================================================

/**
 * Mark a message as seen
 */
export function useMarkMessageAsSeen() {
  return useMutation({
    mutationFn: (messageId: string) => chatService.markMessageAsSeen(messageId),
  });
}

// ============================================================================
// Subscription Hooks
// ============================================================================

/**
 * Get subscriptions for a chat
 */
export function useChatSubscriptions(chatId: string | null) {
  return useQuery({
    queryKey: chatKeys.subscriptions(chatId || ''),
    queryFn: () => chatService.listSubscriptions(chatId!),
    enabled: !!chatId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get my subscriptions across all chats
 */
export function useMySubscriptions() {
  return useQuery({
    queryKey: chatKeys.mySubscriptions(),
    queryFn: chatService.getMySubscriptions,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create a subscription
 */
export function useCreateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chatId,
      data,
    }: {
      chatId: string;
      data: SubscriptionCreate;
    }) => chatService.createSubscription(chatId, data),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.subscriptions(chatId),
      });
      queryClient.invalidateQueries({ queryKey: chatKeys.mySubscriptions() });
    },
  });
}

/**
 * Update a subscription
 */
export function useUpdateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      data,
    }: {
      subscriptionId: string;
      data: SubscriptionUpdate;
    }) => chatService.updateSubscription(subscriptionId, data),
    onSuccess: (subscription) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.subscriptions(subscription.chat_id),
      });
      queryClient.invalidateQueries({ queryKey: chatKeys.mySubscriptions() });
    },
  });
}

/**
 * Delete a subscription
 */
export function useDeleteSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
    }: {
      subscriptionId: string;
      chatId: string;
    }) => chatService.deleteSubscription(subscriptionId),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.subscriptions(chatId),
      });
      queryClient.invalidateQueries({ queryKey: chatKeys.mySubscriptions() });
    },
  });
}

// ============================================================================
// Chat Types System Hooks
// ============================================================================

/**
 * Find or create a 1-1 chat with another user
 * Returns existing chat if it exists, or creates a new one
 */
export function useFindOrCreateOneToOne() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (otherUserId: string) =>
      chatService.findOrCreateOneToOne(otherUserId),
    onSuccess: (chat) => {
      // Cache the chat detail
      queryClient.setQueryData(chatKeys.detail(chat.id), chat);
      // Invalidate the chat list to include the new/existing chat
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    },
  });
}

/**
 * Auto-join general chat (idempotent)
 * Adds current user as member of the general tenant chat
 */
export function useJoinGeneralChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => chatService.joinGeneralChat(),
    onSuccess: () => {
      // Invalidate chat list to refresh membership status
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    },
  });
}

// ============================================================================
// Member Management Hooks
// ============================================================================

/**
 * Get members of a chat
 */
export function useChatMembers(chatId: string | null) {
  return useQuery({
    queryKey: chatKeys.members(chatId || ''),
    queryFn: () => chatService.listMembers(chatId!),
    enabled: !!chatId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Add members to a group chat (admin/owner only)
 */
export function useAddMembers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, userIds }: { chatId: string; userIds: string[] }) =>
      chatService.addMembers(chatId, userIds),
    onSuccess: (_, { chatId }) => {
      // Invalidate members list
      queryClient.invalidateQueries({ queryKey: chatKeys.members(chatId) });
      // Invalidate chat detail to update participant_count
      queryClient.invalidateQueries({ queryKey: chatKeys.detail(chatId) });
    },
  });
}

/**
 * Remove a member from a group chat (admin/owner only)
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, userId }: { chatId: string; userId: string }) =>
      chatService.removeMember(chatId, userId),
    onSuccess: (_, { chatId }) => {
      // Invalidate members list
      queryClient.invalidateQueries({ queryKey: chatKeys.members(chatId) });
      // Invalidate chat detail to update participant_count
      queryClient.invalidateQueries({ queryKey: chatKeys.detail(chatId) });
    },
  });
}

/**
 * Update member role (owner only)
 * Can promote to admin or demote to member
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chatId,
      userId,
      role,
    }: {
      chatId: string;
      userId: string;
      role: ChatMemberRole.ADMIN | ChatMemberRole.MEMBER;
    }) => chatService.updateMemberRole(chatId, userId, role),
    onSuccess: (_, { chatId }) => {
      // Invalidate members list to show updated role
      queryClient.invalidateQueries({ queryKey: chatKeys.members(chatId) });
      // Invalidate chat detail to update my_role if changed
      queryClient.invalidateQueries({ queryKey: chatKeys.detail(chatId) });
    },
  });
}
